import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// ============================================
// CREDIT NOTE PDF — printable credit note document
// Rendered client-side with @react-pdf/renderer (same pattern as invoices).
// ============================================

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'R0.00';
  return `R${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, color: '#1a2a3a', fontFamily: 'Helvetica' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  brand: { fontSize: 16, color: '#0A1929' },
  brandAccent: { color: '#00A86B' },
  docTitle: { fontSize: 22, color: '#EF4444', fontWeight: 'bold', marginBottom: 2 },
  docSub: { fontSize: 9, color: '#64748B' },
  section: { marginTop: 18 },
  sectionTitle: { fontSize: 10, fontWeight: 'bold', color: '#0A1929', marginBottom: 6, textTransform: 'uppercase' },
  grid: { flexDirection: 'row', justifyContent: 'space-between' },
  col: { width: '48%' },
  line: { marginBottom: 2 },
  label: { color: '#64748B' },
  table: { marginTop: 8, borderTop: '1pt solid #CBD5E1' },
  row: { flexDirection: 'row', borderBottom: '1pt solid #E2E8F0', paddingVertical: 5 },
  th: { fontWeight: 'bold', color: '#0A1929', backgroundColor: '#F1F5F9', paddingVertical: 6 },
  colDesc: { width: '55%', paddingRight: 6 },
  colNum: { width: '15%', textAlign: 'right' },
  colAmt: { width: '30%', textAlign: 'right' },
  totals: { marginTop: 12, alignItems: 'flex-end' },
  totalRow: { flexDirection: 'row', width: 220, justifyContent: 'space-between', paddingVertical: 2 },
  grand: { fontWeight: 'bold', fontSize: 12, color: '#EF4444' },
  footer: { marginTop: 30, borderTop: '1pt solid #CBD5E1', paddingTop: 10, fontSize: 8, color: '#94A3B8' },
  reasonBox: { backgroundColor: '#FEF2F2', borderColor: '#FECACA', borderWidth: 1, padding: 8, borderRadius: 4, marginTop: 8 }
});

function CreditNotePDF({ creditNote, company = {} }) {
  const cn = creditNote || {};
  const client = cn.client || {};
  const items = cn.items || [];
  const showVat = parseFloat(cn.vat_amount || 0) > 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            <Text style={styles.brand}>ONE <Text style={styles.brandAccent}>PURPOSE</Text> LOGISTICS</Text>
            <Text style={styles.docSub}>{company.company_name || 'One Purpose Logistics'}</Text>
            <Text style={styles.docSub}>{company.registration_number || ''}</Text>
            <Text style={styles.docSub}>{company.physical_address || ''}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={styles.docTitle}>CREDIT NOTE</Text>
            <Text style={styles.docSub}>No: {cn.credit_note_number || ''}</Text>
            <Text style={styles.docSub}>Date: {cn.credit_date || ''}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.grid}>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Credited To</Text>
              <Text style={{ fontWeight: 'bold' }}>{client.company_name || '—'}</Text>
              {client.physical_address && <Text>{client.physical_address}</Text>}
              <Text>VAT: {client.vat_number || '—'}</Text>
              {client.contact_email && <Text>{client.contact_email}</Text>}
            </View>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Reference</Text>
              <Text>Original invoice: {cn.invoice?.invoice_number || cn.invoice?.proforma_number || '—'}</Text>
              {cn.quote?.quote_number && <Text>Quote: {cn.quote.quote_number}</Text>}
              {cn.job?.job_number && <Text>Job: {cn.job.job_number}</Text>}
            </View>
          </View>
        </View>

        {cn.reason && (
          <View style={styles.reasonBox}>
            <Text><Text style={{ fontWeight: 'bold' }}>Reason:</Text> {cn.reason}</Text>
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Credit Line Items</Text>
          <View style={styles.table}>
            <View style={[styles.row, styles.th]}>
              <Text style={styles.colDesc}>Description</Text>
              <Text style={styles.colNum}>Qty</Text>
              <Text style={styles.colAmt}>Amount</Text>
            </View>
            {(items.length === 0) && (
              <View style={styles.row}><Text style={styles.colDesc}>Credit for invoice {cn.invoice?.invoice_number || ''}</Text><Text style={styles.colAmt}>{formatCurrency(cn.total_amount)}</Text></View>
            )}
            {items.map((it, i) => (
              <View key={i} style={styles.row}>
                <Text style={styles.colDesc}>{it.description || '—'}</Text>
                <Text style={styles.colNum}>{it.quantity ?? 1}</Text>
                <Text style={styles.colAmt}>{formatCurrency(it.total ?? it.unit_price)}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.totals}>
          <View style={styles.totalRow}><Text>Subtotal</Text><Text>{formatCurrency(cn.subtotal)}</Text></View>
          {showVat && (
            <View style={styles.totalRow}><Text>VAT (15%)</Text><Text>{formatCurrency(cn.vat_amount)}</Text></View>
          )}
          <View style={[styles.totalRow, { borderTop: '1pt solid #CBD5E1', paddingTop: 6 }]}>
            <Text style={styles.grand}>Total Credit</Text>
            <Text style={styles.grand}>{formatCurrency(cn.total_amount)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>This credit note reduces the amount owed by the above-named client. It will be applied against the re-quoted / new invoice for this work.</Text>
          <Text>One Purpose Logistics • {company.contact_email || 'info@onepurposelogistics.com'} • {company.contact_phone || '+27 63 598 5855'}</Text>
        </View>
      </Page>
    </Document>
  );
}

export default CreditNotePDF;

